/**
 * Utility functions for downloading and processing parquet files using hyparquet
 */

import { parquetRead } from "hyparquet";
import { compressors } from "hyparquet-compressors";
import { pick } from "lodash-es";

import { tdApi } from "@src/services/api/tdApi";

import type { BacktestParquetRecord, ProcessedCacheData } from "@src/types";

/**
 * Convert a parquet row value to the appropriate JavaScript type.
 * Handles Date objects (timestamps), nulls, numbers, and strings.
 * Returns null for unparseable values to make data issues visible.
 */
const processParquetValue = (value: unknown, columnName: string): number | string | null => {
  // Handle null/undefined - preserve as null for signals and levels
  if (value === null || value === undefined) {
    return null;
  }

  // Handle Date objects (timestamps from parquet)
  if (value instanceof Date) {
    return value.getTime();
  }

  // Handle numbers directly
  if (typeof value === "number") {
    return value;
  }

  if (typeof value === "string") {
    // Handle date strings - convert to timestamp
    // Use specific suffixes to avoid false matches like "lifetime", "overtime"
    if (
      columnName.endsWith(" time") ||
      columnName.endsWith("_time") ||
      columnName.endsWith("_ts")
    ) {
      const parsed = new Date(value);
      if (isNaN(parsed.getTime())) {
        console.warn(`[Parquet] Failed to parse date string for column "${columnName}":`, value);
        return null;
      }
      return parsed.getTime();
    }

    // Try numeric conversion - if it fails, keep as string (e.g. entry_reason_key)
    const numValue = Number(value);
    if (!isNaN(numValue)) {
      return numValue;
    }
    return value;
  }

  return null;
};

// A chart preview carries candles and indicators only: the money curve comes from the full cache
const PREVIEW_COLUMNS = ["open time", "close time", "open", "high", "low", "close", "volume"];
const FULL_CACHE_COLUMNS = [...PREVIEW_COLUMNS, "capital", "returns"];

/**
 * Process ArrayBuffer parquet data into BacktestParquetRecord array.
 * FULLY DYNAMIC: Extracts ALL columns from the parquet file.
 * This allows any strategy to define any indicators without frontend changes.
 *
 * @param arrayBuffer - Raw parquet data
 * @param requiredColumns - Columns the file must carry; the full cache's set unless a caller names another
 * @returns Promise that resolves to the parsed ProcessedCacheData
 */
const processParquetArrayBuffer = async (
  arrayBuffer: ArrayBuffer,
  requiredColumns: string[] = FULL_CACHE_COLUMNS,
): Promise<ProcessedCacheData> => {
  const cacheData: BacktestParquetRecord[] = [];

  await parquetRead({
    file: arrayBuffer,
    compressors, // Add compression support for ZSTD, GZIP, BROTLI, etc.
    rowFormat: "object",
    onComplete: (data: Record<string, unknown>[]) => {
      if (data.length === 0) {
        console.warn("[Parquet] No data rows in parquet file");
        return;
      }

      // Get ALL column names from first row - fully dynamic extraction
      const columns = Object.keys(data[0]);
      // Validate required columns exist
      const missingColumns = requiredColumns.filter((col) => !columns.includes(col));
      if (missingColumns.length > 0) {
        console.error("[Parquet] Missing required columns:", missingColumns);
        throw new Error(`Parquet file missing required columns: ${missingColumns.join(", ")}`);
      }

      // Convert each row dynamically - no hardcoded columns
      data.forEach((row) => {
        const record: Record<string, number | string | null> = {};

        // Extract all columns dynamically
        columns.forEach((col) => {
          record[col] = processParquetValue(row[col], col);
        });

        cacheData.push(record as BacktestParquetRecord);
      });

      console.log(`[Parquet] Processed ${cacheData.length} rows with ${columns.length} columns`);
    },
  });

  return cacheData;
};

const R2_VIA_API_PREFIX = "https://api.tradingdroid.net/r2/";
const R2_DIRECT_PREFIX = "https://r2.tradingdroid.net/";
const DIRECT_R2_HEADER_TIMEOUT_MS = 8000;
const PREVIEW_CANDLE_VALUES = ["open", "high", "low", "close", "volume", "quote_volume"];

/**
 * Stored cache URLs may point at the API host's R2 proxy, which serves the same object several times
 * slower than the bucket's own domain. Fetch those from the bucket directly.
 */
export const directR2Url = (url: string): string =>
  url.startsWith(R2_VIA_API_PREFIX) ? R2_DIRECT_PREFIX + url.slice(R2_VIA_API_PREFIX.length) : url;

/** Download preview bytes through the authenticated API client. */
export const downloadExecutionPreview = async (
  executionId: number,
): Promise<ProcessedCacheData | null> => {
  const bytes = await tdApi.getExecutionChartPreview(executionId);
  if (bytes === null) return null;
  const data = await processParquetArrayBuffer(bytes, PREVIEW_COLUMNS);
  return data.length > 0 ? data : null;
};

/**
 * Append newer preview rows. At the cache's last candle, update OHLCV and available quote volume
 * while retaining its indicator, trade, and performance columns.
 */
export const joinPreview = (
  cache: ProcessedCacheData | null,
  preview: ProcessedCacheData | null,
): ProcessedCacheData | null => {
  if (!preview?.length || !cache?.length) return cache;
  const cacheLast = cache[cache.length - 1];
  const cacheEnd = cacheLast["open time"];
  const newer = preview.filter((row) => row["open time"] > cacheEnd);
  // The cache was built up to the moment it ran, so its last candle can be one still forming. The
  // preview has only finished candles, so its copy of that candle replaces the cache's prices.
  const finished = preview.find((row) => row["open time"] === cacheEnd);
  // Without that candle, join only a preview that starts where the cache's last candle closes: a later start
  // would leave a gap the chart does not mark. A candle closes up to one second before the next one opens.
  if (!finished && !(newer.length > 0 && newer[0]["open time"] - cacheLast["close time"] <= 1000)) {
    return cache;
  }
  const cacheOnlyColumns = Object.keys(cache[0]).filter((col) => !(col in preview[0]));
  // NaN, not null: the chart draws a null as 0 on a dense line, and a NaN as a gap
  const blanks = Object.fromEntries(cacheOnlyColumns.map((col) => [col, NaN]));
  const last = finished ? { ...cacheLast, ...pick(finished, PREVIEW_CANDLE_VALUES) } : cacheLast;
  return [...cache.slice(0, -1), last, ...newer.map((row) => ({ ...blanks, ...row }))];
};

/**
 * Downloads a parquet file from a URL and converts it to JSON data
 * @param parquetUrl - The URL to the parquet file
 * @returns Promise that resolves to the parsed ProcessedCacheData
 */
export const downloadAndProcessParquet = async (
  parquetUrl: string,
): Promise<ProcessedCacheData> => {
  try {
    const fetchUrl = directR2Url(parquetUrl);
    console.log(`[Parquet] Fetching: ${fetchUrl}`);
    // The stored proxy address still works where the bucket domain is blocked, so it is the fallback
    let response: Response;
    if (fetchUrl === parquetUrl) {
      response = await fetch(parquetUrl);
    } else {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), DIRECT_R2_HEADER_TIMEOUT_MS);
      let direct: Response | null = null;
      try {
        direct = await fetch(fetchUrl, { signal: controller.signal });
      } catch {
        // A rejected or aborted direct request uses the stored proxy address below.
      } finally {
        clearTimeout(timeout);
      }
      response = direct?.ok ? direct : await fetch(parquetUrl);
    }

    if (!response.ok) {
      throw new Error(`Failed to download parquet file: ${response.status} ${response.statusText}`);
    }

    const arrayBuffer = await response.arrayBuffer();

    // Process and return
    return await processParquetArrayBuffer(arrayBuffer);
  } catch (error) {
    console.error("[Parquet] Error downloading/processing parquet file:", error);
    throw new Error(
      `Failed to process parquet file: ${error instanceof Error ? error.message : String(error)}`,
    );
  }
};
